import { configureStore } from '@reduxjs/toolkit';
import cookies from 'js-cookie';
import createSagaMiddleware from 'redux-saga';
import { createTracker } from 'redux-segment';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  cognitoSignInCallbackRedux,
  fetchUser,
  setUserToken,
} from './js/ducks/users';
import reducer from './js/reducers';
import rootSaga from './js/sagas';

import HttpService from './js/services/http-service';
import PrismicService from './js/services/prismic-service';
import './js/modules/cognito';

import { createAuthorization } from './js/httpMiddleware/authorization';
import jsonPayload from './js/httpMiddleware/json-payload';

import { AUTH_TOKEN } from './js/constants-togather/auth';

import config from './app-config';

import App from './js/app';

import './css/app.scss';

import './css/fonts.scss';

// Polyfill for IntersectionObserver
require('intersection-observer');

// Usual Redux stuff
const sagaMiddleware = createSagaMiddleware();

const store = configureStore({
  devTools: config.deployedEnvironment === 'local',
  reducer,
  middleware: (getDefaultMiddleware) => [
    sagaMiddleware,
    ...getDefaultMiddleware(),
    createTracker(),
  ],
});

sagaMiddleware.run(rootSaga);

//  Middlewares specific to the custom http service (see http-service.js)
const httpMiddleware = [jsonPayload, createAuthorization()];

export const httpService = new HttpService(
  config.baseApiUri,
  httpMiddleware,
  store
);

export const prismicService = new PrismicService();

const appElement = document.getElementById('app');

if (!appElement) {
  throw new Error('App root element not found');
}

const root = createRoot(appElement);

const renderApp = (AppComponent) => {
  root.render(<AppComponent store={store} />);
};

const token = cookies.get(AUTH_TOKEN);

// if we have an existing identity then we should update it
if (token) {
  // `setUserToken` is synchronous, so `user.token` is seeded before the route
  // guards run on first render.
  store.dispatch(setUserToken(token));
  // Check that token is still valid, otherwise user will be logged out
  store.dispatch(fetchUser(token));
  renderApp(App);
} else {
  // No legacy/impersonation cookie: restore the shared Cognito session if one
  // exists (e.g. inherited via a cross-FE redirect). This is async, so render
  // only AFTER it settles — otherwise the route guards run against the initial
  // visitor state and redirect a soon-to-be-authenticated user to logout.
  // Resolves immediately when anonymous (no Amplify session → no-op).
  store.dispatch(cognitoSignInCallbackRedux()).finally(() => renderApp(App));
}

if (module.hot) {
  module.hot.accept('./js/app', () => {
    // eslint-disable-next-line global-require
    renderApp(require('./js/app').default);
  });

  module.hot.accept('./js/reducers', () => {
    // eslint-disable-next-line global-require
    store.replaceReducer(require('./js/reducers').default);
  });
}
